import React, { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import TableSearchState from "@/scenes/OrderManagement/redux/states/TableSearchState";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { Form, Popover, Spin, Tag } from "antd";
import { useLinkedProductSearchFieldsStore } from "./LinkedProductSearchFieldsStore";
import AutoDismissToolTip from "@/components/AutoDismissToolTip";
import Highlight from "@/components/Highlight";
import {
    FlexibleSelectData,
    SelectType,
} from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/FlexibleSelect/type";
import FlexibleSelect from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/FlexibleSelect";
import useInitialDataSourceValue from "./useInitialDataSourceValue";
import { useUpdateEffect } from "ahooks";
import { difference, uniq } from "lodash";
import ProxyImage from "@/components/ProxyImage";
import { useGlobalStore } from "@/store/hooks";

interface OrderItemTitleFieldProps {
    tab: OrderTab;
    shopId?: number;
}

interface OrderItemTitleFieldCoreProps extends Pick<TableSearchState, "orderItemTitleValue"> {
    shopId?: number;
    tab: OrderTab;
}

const ORDER_ITEM_TITLE_FIELD_MULTIPLE = "ORDER_ITEM_TITLE_FIELD_MULTIPLE";

const OrderItemTitleFieldsCore: React.FC<OrderItemTitleFieldCoreProps> = observer((props) => {
    const { orderItemTitleValue, shopId, tab } = props;
    const orderManagementStore = useOrderManagementStore();
    const store = useLinkedProductSearchFieldsStore();
    const { onFocusLoad, products, productMap } = store;
    const [searchText, updateSearchText] = useState("");
    const { orderItemTitle, productKey, productKeys, titleFieldSkuKeys } = orderItemTitleValue || {};
    const globalStore = useGlobalStore();

    const filteredProducts = useMemo(() => {
        return products?.filter((it) => {
            if (it.titles?.length > 1) {
                // 有多标题时，多个标题要参与过滤
                return it.titles?.some((it) => it?.includes(searchText)) || it.title?.includes(searchText); // it.title可能是简称
            }

            return it.title?.includes(searchText);
        });
    }, [products, searchText]);

    const dataSource = useMemo<FlexibleSelectData[]>(
        () =>
            filteredProducts.map(({ title, count, originalId, productKey, picture, titles }) => {
                return {
                    label: (
                        <>
                            {picture && store.loadProductPicture ? (
                                <span className="mr5">
                                    <ProxyImage width={40} height={40} src={picture} />
                                </span>
                            ) : null}

                            <span>
                                <span className="ml5 text-gray">{originalId} / </span>
                                <Highlight text={title} highlightTexts={[searchText]} />
                                {titles?.length > 1 ? (
                                    <Popover
                                        zIndex={9999}
                                        content={
                                            <div onClick={(e) => e.stopPropagation()}>
                                                {titles.map((title, index) => (
                                                    <p key={index} className="mb3">
                                                        <Highlight text={title} highlightTexts={[searchText]} />
                                                    </p>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <Tag variant="outlined" className="ml3" style={{ margin: "0 0 0 3px" }}>
                                            多名称
                                        </Tag>
                                    </Popover>
                                ) : null}
                            </span>

                            {count ? <span className="text-error ml5">{count}件</span> : null}
                        </>
                    ),
                    value: productKey,
                    inputText: title,
                };
            }),
        [filteredProducts, store.loadProductPicture],
    );

    const initialDataSourceValue = useInitialDataSourceValue(dataSource, searchText);

    const inputTextMap = useMemo<Map<string, string>>(() => {
        const map = new Map();

        productMap.forEach((it) => {
            map.set(it.productKey, it.title);
        });

        return map;
    }, [productMap]);

    const handleClear = useCallback(() => {
        orderManagementStore.table.form.setFieldsValue({
            orderItemTitleValue: {
                orderItemTitle: "",
                productKey: undefined,
                productKeys: [],
                titleFieldSkuKeys: undefined,
                isIndeterminate: false,
                isSelectAll: false,
            },
            skuPropertiesValue: {
                skuProperties: "",
                skuKey: undefined,
                skuKeys: [],
                skuPropertiesFieldSkuKeys: undefined,
                isIndeterminate: false,
                isSelectAll: false,
            },
            orderItemExternalIdValue: {
                orderItemExternalId: "",
                prefixedExternalId: undefined,
                prefixedExternalIds: [],
                externalIdFieldSkuKeys: undefined,
                isIndeterminate: false,
                isSelectAll: false,
            },
        });
    }, []);

    function handleMultipleChange(multiple: boolean) {
        updateSearchText("");
        handleClear();

        if (multiple) {
            localStorage.setItem(ORDER_ITEM_TITLE_FIELD_MULTIPLE, "1");
        } else {
            localStorage.removeItem(ORDER_ITEM_TITLE_FIELD_MULTIPLE);
        }
    }

    function handleFocusLoad() {
        updateSearchText("");

        onFocusLoad();
    }

    function handleSimpleTitleChange(checked: boolean) {
        updateSearchText("");
        handleClear();

        store.updateLoadProductSimpleTitle(checked);
    }

    useUpdateEffect(() => {
        if (!orderManagementStore?.table?.form) return;

        const isSelectAll =
            productKeys?.length === initialDataSourceValue?.length &&
            productKeys?.length > 0 &&
            initialDataSourceValue?.length > 0;
        const isIndeterminate = !isSelectAll && productKeys?.length > 0 && initialDataSourceValue?.length > 0;

        orderManagementStore.table.form.setFieldsValue({
            orderItemTitleValue: {
                isSelectAll,
                isIndeterminate,
            },
        });
    }, [productKeys]);

    function handleSelectAllChange(checked: boolean) {
        let nextTitleFieldSkuKeys: string[] = [...(titleFieldSkuKeys || [])];
        let nextProductKeys: string[] = [...(productKeys || [])];

        handleClear();

        if (checked) {
            /** 在当前展示的数据源中进行全选 */
            dataSource.forEach(({ value }) => {
                const simpleProduct = productMap.get(value);
                if (!simpleProduct) return;

                nextProductKeys.push(simpleProduct.productKey);

                simpleProduct.skus.forEach(({ skuKey }) => {
                    nextTitleFieldSkuKeys.push(skuKey);
                });
            });

            /** 如果下拉中存在已选中的值，这里虚再次取消，会出现值重复的情况，需要去重 */
            nextTitleFieldSkuKeys = uniq(nextTitleFieldSkuKeys);
            nextProductKeys = uniq(nextProductKeys);
        } else {
            if (searchText) {
                /** 有搜索条件下取消全选，将当前下拉数据源剔除 */
                const unSelectedProductKeys: string[] = [];
                const unSelectedTitleFieldSkuKeys: string[] = [];

                dataSource.forEach(({ value }) => {
                    const simpleProduct = productMap.get(value);
                    if (!simpleProduct) return;

                    unSelectedProductKeys.push(simpleProduct.productKey);

                    simpleProduct.skus.forEach(({ skuKey }) => {
                        unSelectedTitleFieldSkuKeys.push(skuKey);
                    });
                });

                nextProductKeys = difference(nextProductKeys, unSelectedProductKeys);
                nextTitleFieldSkuKeys = difference(nextTitleFieldSkuKeys, unSelectedTitleFieldSkuKeys);
            } else {
                /** 没有搜索条件下取消全选，直接置空 */
                nextProductKeys = [];
                nextTitleFieldSkuKeys = [];
            }
        }

        orderManagementStore.table.form.setFieldsValue({
            orderItemTitleValue: {
                orderItemTitle: "",
                productKey: undefined,
                productKeys: nextProductKeys,
                titleFieldSkuKeys: nextTitleFieldSkuKeys,
            },
        });
    }

    function handleChange(orderItemTitle: string, productKey: string, productKeys: string[]) {
        orderManagementStore.table.form.setFieldsValue({
            skuPropertiesValue: {
                skuPropertiesFieldSkuKeys: undefined,
            },
            orderItemExternalIdValue: {
                externalIdFieldSkuKeys: undefined,
            },
            orderItemTitleValue: {
                titleFieldSkuKeys: undefined,
            },
        });

        if (productKeys) {
            const titleFieldSkuKeys = [];

            products.forEach(({ productKey, skus }) => {
                if (productKeys.includes(productKey)) {
                    skus.forEach(({ skuKey }) => {
                        if (skuKey) {
                            titleFieldSkuKeys.push(skuKey);
                        }
                    });
                }
            });

            orderManagementStore.table.form.setFieldsValue({
                skuPropertiesValue: {
                    skuProperties: "",
                    skuKeys: [],
                    isSelectAll: false,
                    isIndeterminate: false,
                },
                orderItemExternalIdValue: {
                    orderItemExternalId: "",
                    prefixedExternalIds: [],
                    isSelectAll: false,
                    isIndeterminate: false,
                },
                orderItemTitleValue: {
                    orderItemTitle: "",
                    productKeys,
                    titleFieldSkuKeys,
                },
            });
        } else if (productKey) {
            const skuKeys = [];

            products.forEach((product) => {
                if (product.productKey === productKey) {
                    product.skus.forEach(({ skuKey }) => {
                        skuKeys.push(skuKey);
                    });
                }
            });

            orderManagementStore.table.form.setFieldsValue({
                orderItemExternalIdValue: {
                    orderItemExternalId: "",
                    prefixedExternalId: undefined,
                    prefixedExternalIds: [],
                    isSelectAll: false,
                    isIndeterminate: false,
                    externalIdFieldSkuKeys: undefined,
                },
                skuPropertiesValue: {
                    skuProperties: "",
                    skuKey: undefined,
                    skuKeys: [],
                    isSelectAll: false,
                    isIndeterminate: false,
                    skuPropertiesFieldSkuKeys: undefined,
                },
                orderItemTitleValue: {
                    orderItemTitle: "",
                    productKey,
                    productKeys: [],
                    titleFieldSkuKeys: skuKeys,
                    isSelectAll: false,
                    isIndeterminate: false,
                },
            });
        } else {
            orderManagementStore.table.form.setFieldsValue({
                skuPropertiesValue: {
                    skuProperties: "",
                    skuKey: undefined,
                    skuKeys: [],
                    isSelectAll: false,
                    isIndeterminate: false,
                    skuPropertiesFieldSkuKeys: undefined,
                },
                orderItemExternalIdValue: {
                    orderItemExternalId: "",
                    prefixedExternalId: undefined,
                    prefixedExternalIds: [],
                    isSelectAll: false,
                    isIndeterminate: false,
                    externalIdFieldSkuKeys: undefined,
                },
                orderItemTitleValue: {
                    orderItemTitle,
                    productKey: undefined,
                    productKeys: [],
                    isSelectAll: false,
                    isIndeterminate: false,
                    titleFieldSkuKeys: undefined,
                },
            });
        }
    }

    const defaultMultiple = !!localStorage.getItem(ORDER_ITEM_TITLE_FIELD_MULTIPLE);

    const el = (
        <Form.Item name="orderItemTitleValue" label={<span className="search-field-label">商品名称</span>}>
            <FlexibleSelect
                className="order-item-title-select-container"
                data={dataSource}
                onFocus={handleFocusLoad}
                onChange={handleChange}
                defaultMultiple={defaultMultiple}
                onMultipleChange={handleMultipleChange}
                onSearch={updateSearchText}
                inputValue={orderItemTitle}
                singleValue={productKey}
                multipleValue={productKeys}
                onClear={handleClear}
                inputTextMap={inputTextMap}
                shopId={shopId}
                type={SelectType.ORDER_ITEM_TITLE}
                onEnabledTopSellingProductPrintingChange={store.switchTopSellingProductPrinting}
                enabledTopSellingProductPrinting={store.enabledTopSellingProductPrinting}
                needSimpleTitle={store.loadProductSimpleTitle}
                onSimpleTitleChange={handleSimpleTitleChange}
                searchText={searchText}
                onSelectAllChange={handleSelectAllChange}
                isSelectAll={orderItemTitleValue?.isSelectAll}
                isIndeterminate={orderItemTitleValue?.isIndeterminate}
                itemHeight={!store.loadProductPicture ? 25 + 3 : 44 + 3}
                showTitlePic={store.loadProductPicture}
                onShowTitlePicChange={store.updateLoadProductPicture}
            />
        </Form.Item>
    );

    if (tab === OrderTab.PENDING_SHIPMENT) {
        return (
            <AutoDismissToolTip
                title="仅支持下拉选择待发货商品的信息，已发货已取消等商品信息不自动显示"
                trigger="click"
            >
                {el}
            </AutoDismissToolTip>
        );
    }

    return el;
});

const OrderItemTitleField: React.FC<OrderItemTitleFieldProps> = function (props) {
    const { tab, shopId } = props;
    const store = useLinkedProductSearchFieldsStore();
    const { loading } = store;
    const orderItemTitleValue = Form.useWatch("orderItemTitleValue");

    return (
        <Spin spinning={loading}>
            <OrderItemTitleFieldsCore orderItemTitleValue={orderItemTitleValue} shopId={shopId} tab={tab} />
        </Spin>
    );
};

export default observer(OrderItemTitleField);
